import { useContext } from 'react';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { logger } from 'src/logger';
import { useGetCaseDetailsQuery } from 'src/state/slice/caseLifecycleApi';
import {
  IViewCaseRequest,
  IViewCaseResponse,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useGetSchemaQuery } from 'src/state/slice/ddmServiceApi';
import {
  DDM_CPR_CLIENT_ID,
  PAGE_SCOPE,
  UI_CLIENTS,
} from 'src/constants/GlobalConstants';
import { IGetSchemaResponse } from 'src/integration/ddm/DDMModel';
import { PageContext } from 'src/state/providers/PageProvider';

export type ViewCaseAggregatedQueryResponse = {
  caseDetailsResponse?: IViewCaseResponse;
  ddmSchemaResponse?: IGetSchemaResponse;
  error?: FetchBaseQueryError | SerializedError | unknown;
  isError?: boolean;
  isLoading: boolean;
};

export const useViewCaseAggregatedQuery = (
  caseId: string,
): ViewCaseAggregatedQueryResponse => {
  const userContext = useContext(AuthContext);
  const pageContext = useContext(PageContext);

  const viewCaseRequest: IViewCaseRequest = {
    userId: userContext.customerId,
    accountId: userContext.preferredAccountId,
    caseId: caseId,
    clientId: userContext.clientId,
  };

  logger.info(
    `Calling GetCaseDetails for userId: ${userContext.customerId}. Request is`,
    { viewCaseRequest },
  );
  const {
    data: caseDetailsResponse,
    isLoading: isCaseDetailsLoading,
    error: caseDetailsError,
    isError: isCaseDetailsError,
  } = useGetCaseDetailsQuery(viewCaseRequest);

  const caseDetailsExecutionCompleted =
    !isCaseDetailsLoading && caseDetailsResponse ? true : false;

  //Make Calls to the DDM to fetch the schema for this case.
  const {
    isLoading: isGetSchemaLoading,
    data: ddmSchemaResponse,
    isError: isGetSchemaError,
    error: getSchemaError,
  } = useGetSchemaQuery(
    {
      clientId: DDM_CPR_CLIENT_ID,
      schemaKey: caseDetailsResponse?.ddmSchemaId,
      schemaVersion: caseDetailsResponse?.ddmSchemaVersion,
      isPublicPage: PAGE_SCOPE.PUBLIC === pageContext.pageScope,
      isRegulatorCentralApp:
        userContext.clientId === UI_CLIENTS.RegulatorCentral,
    },
    { skip: !caseDetailsExecutionCompleted },
  );

  return {
    caseDetailsResponse,
    ddmSchemaResponse,
    error: caseDetailsError || getSchemaError,
    isError: isCaseDetailsError || isGetSchemaError,
    isLoading: isCaseDetailsLoading || isGetSchemaLoading,
  };
};
